import type { Level, LevelKind } from './levels';

/**
 * The popular convention: green where price bounced, red where it was
 * rejected. In this design system green is the lime `--primary`/`--success`
 * and red is `--destructive` — the same long/short mapping the liquidation
 * bands use, so the two overlays agree on what each colour means. They are
 * told apart by shape: liq zones are fills, S/R levels are crisp lines.
 */
export const SUPPORT_COLOR = '#D3FF12';
export const RESISTANCE_COLOR = '#FF2D64';

/** A level that held this often is called out in the legend; the lines themselves stay uniform. */
export const STRONG_TOUCHES = 3;

export function levelColor(kind: LevelKind): string {
    return kind === 'support' ? SUPPORT_COLOR : RESISTANCE_COLOR;
}

export function isStrong(level: Pick<Level, 'touches'>): boolean {
    return level.touches >= STRONG_TOUCHES;
}

export function heldTimes(touches: number): string {
    return `held ${touches}×`;
}

/** The label drawn on the chart: what it is and how often it held. */
export function levelLabel(level: Pick<Level, 'kind' | 'touches'>): string {
    const what = level.kind === 'support' ? 'Support' : 'Resistance';
    return `${what} · ${heldTimes(level.touches)}`;
}

/** The nearest level on each side of the price: the two a trader looks at first. */
export function nearestBySide(levels: readonly Level[]): {
    resistance: Level | null;
    support: Level | null;
} {
    const nearest = (kind: LevelKind) =>
        levels
            .filter((l) => l.kind === kind)
            .sort((a, b) => Math.abs(a.distancePct) - Math.abs(b.distancePct))[0] ??
        null;
    return { resistance: nearest('resistance'), support: nearest('support') };
}
